import { LegalBottom } from "@/components/LegalFooter";
import { ZonesSection } from "@/components/ZonesSection";
import { toast } from "sonner";
import { formatPlate } from "@/components/calculator/PlateStep";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Car,
  Bike,
  HeartPulse,
  Shield,
  HardHat,
  Home,
  ShieldCheck,
  Landmark,
  PiggyBank,
  TrendingUp,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  Search,
  Menu,
  X,
  ArrowRight,
  CheckCircle2,
  Play,
  Headphones,
  Smartphone,
  Zap,
  Users,
  Award,
  Star,
  Quote,
  Clock,
  Building2,
} from "lucide-react";
import axios from "axios";
import { SimpleLogo, HeaderLogo, AnimatedTheGoodDealLogo } from "@/components/AnimatedLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo JYVIS
const JYVIS_LOGO = "/logo.svg";

// Logo TheGoodDeal.fr
const THEGOODDEAL_LOGO = "/images/thegooddeal.jpg";

// Social Media Links
const socialLinks = {
  instagram: "https://www.instagram.com/jyvis.assurances/",
  tiktok: "https://www.tiktok.com/@jyvis.assurances",
  youtube: "https://www.youtube.com/@JyvisAssurances",
  twitter: "https://x.com/JyvisAssurances",
  facebook: "https://www.facebook.com/profile.php?id=100078545120236",
  linkedin: "https://www.linkedin.com/in/jyvis-assurances-206017357/",
};

// Contact Info
const contactInfo = {
  phone: "01 48 20 98 27",
  email: "contact@jyvis-assurances.com",
  address: "47 rue Jean Jaurès",
  city: "93200 Saint-Denis",
};

const insuranceIcons = {
  auto: Car,
  moto: Bike,
  sante: HeartPulse,
  prevoyance: Shield,
  rc_decennale: HardHat,
  multirisque_pro: Building2,
  multirisque: Home,
  cybersecurite: ShieldCheck,
  pret_immobilier: Landmark,
  vie: PiggyBank,
  retraite: TrendingUp,
};

const productImages = {
  auto: "https://images.unsplash.com/photo-1558898268-92ae44e7670e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  moto: "https://images.unsplash.com/photo-1569932353341-b518d82f8a54?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  sante: "https://images.unsplash.com/photo-1638202993928-7267aad84c31?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  multirisque: "https://images.unsplash.com/photo-1729838809728-48566c1ef0e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  cybersecurite: "https://images.unsplash.com/photo-1768839721176-2fa91fdce725?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  retraite: "https://images.unsplash.com/photo-1758686254493-d73c9c2a3049?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

// Podcast data - SEO optimized
const podcastEpisodes = [
  {
    id: 1,
    title: "Comment bien assurer sa voiture en Île-de-France ?",
    duration: "10:25",
    category: "Auto",
    image: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  },
  {
    id: 2,
    title: "Mutuelle santé : ce qui change en 2026",
    duration: "15:30",
    category: "Santé",
    image: "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  },
  {
    id: 3,
    title: "Assurance décennale pour les artisans du 93",
    duration: "12:45",
    category: "Pro",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  },
];

const GOOGLE_REVIEWS_URL = "https://www.google.com/search?q=Jyvis+Assurances";
const GOOGLE_LEAVE_REVIEW_URL = "https://www.google.com/search?q=Jyvis+Assurances";
const GOOGLE_REVIEWS_COUNT = 12;
const GOOGLE_RATING = "4,7";
const openReviewLink = (url) => (url ? window.open(url, "_blank", "noopener") : toast.info("Lien bientôt disponible"));

// Partner logos - Generated high quality logos
const partners = [
  { 
    name: "AXA", 
    logo: "/images/partners/axa.png",
    color: "#00008f"
  },
  { 
    name: "Allianz", 
    logo: "/images/partners/allianz.png",
    color: "#003781"
  },
  { 
    name: "Generali", 
    logo: "/images/partners/generali.png",
    color: "#c8102e"
  },
  { 
    name: "Swiss Life", 
    logo: "/images/partners/swisslife.png",
    color: "#e30613"
  },
  { 
    name: "MAIF", 
    logo: "/images/partners/maif.png",
    color: "#00a19a"
  },
];

export default function LandingPage() {
  const [products, setProducts] = useState([]);
  const [licensePlate, setLicensePlate] = useState("");
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [vehicleType, setVehicleType] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data.products);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  const lookupPlate = async () => {
    if (!licensePlate.trim()) return;
    navigate("/devis/auto", { state: { plate: licensePlate } });
  };

  const startQuote = (type) => {
    if (type === "auto" || type === "moto") {
      navigate(`/devis/${type}`, { state: { plate: licensePlate } });
    } else {
      navigate(`/devis/${type}`);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Blue with JYVIS name */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#102692] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                <img src={JYVIS_LOGO} alt="JYVIS Assurances" className="jyvis-logo h-12 w-auto" />
                <span className="text-white font-heading font-bold text-xl hidden sm:block">JYVIS</span>
              </Link>
              {/* Séparateur + Logo TheGoodDeal animé */}
              <div className="hidden sm:flex items-center gap-3">
                <div className="h-8 w-px bg-white/30" />
                <AnimatedTheGoodDealLogo size="md" />
              </div>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-5">
              <Link to="/professionnels" className="text-white/80 hover:text-white transition-colors font-medium">
                Professionnels
              </Link>
              <Link to="/particuliers" className="text-white/80 hover:text-white transition-colors font-medium">
                Particuliers
              </Link>
              <Link to="/temporaire" className="text-white/80 hover:text-white transition-colors font-medium flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Temporaire
              </Link>
              <Link to="/formation-courtier" className="text-white/80 hover:text-white transition-colors font-medium flex items-center gap-1">
                <Award className="w-4 h-4" />
                Formation
              </Link>
              <a href="#podcasts" className="text-white/80 hover:text-white transition-colors font-medium">
                Podcasts
              </a>
              <a href="#contact" className="text-white/80 hover:text-white transition-colors font-medium">
                Contact
              </a>
              <Link to="/espace-client">
                <Button className="rounded-full bg-white text-[#102692] hover:bg-white/90 font-semibold" data-testid="client-space-btn">
                  Espace client
                </Button>
              </Link>
            </nav>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Nav */}
          {mobileMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="md:hidden pt-4 pb-2 flex flex-col gap-4"
            >
              <Link to="/professionnels" className="text-white/80 py-2">Professionnels</Link>
              <Link to="/particuliers" className="text-white/80 py-2">Particuliers</Link>
              <Link to="/temporaire" className="text-white/80 py-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Temporaire
              </Link>
              <Link to="/formation-courtier" className="text-white/80 py-2 flex items-center gap-2">
                <Award className="w-4 h-4" />
                Formation
              </Link>
              <a href="#podcasts" className="text-white/80 py-2">Podcasts</a>
              <a href="#contact" className="text-white/80 py-2">Contact</a>
              <Link to="/espace-client">
                <Button className="w-full rounded-full bg-white text-[#102692] font-semibold">Espace client</Button>
              </Link>
            </motion.nav>
          )}
        </div>
      </header>

      {/* Hero Section - Video Background with text */}
      <section className="relative min-h-[90vh] flex items-center pt-20 overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="/images/hero-poster.jpg"
            className="w-full h-full object-cover"
            data-testid="hero-video"
          >
            <source src="/images/hero-480.mp4" type="video/mp4" media="(max-width: 767px)" />
            <source src="/images/hero-720.mp4" type="video/mp4" />
          </video>
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#102692]/85 via-[#102692]/75 to-[#102692]/85" />
        </div>
        
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden z-10 pointer-events-none">
          <div className="absolute top-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-20 max-w-7xl mx-auto px-6 py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
                <span className="text-[#F97316]">Jyvis Assurance –</span>
                <br />
                <span className="text-3xl md:text-4xl">Courtier RC Décennale et assurances des professionnels</span>
              </h1>
              <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8 max-w-2xl">
                Courtier indépendant – RC Décennale, multirisque pro, flotte et VTC. Et aussi vos assurances auto, moto, habitation et santé.
              </p>
              
              {/* Key features badges */}
              <div className="flex flex-wrap gap-3 mb-8">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Smartphone className="w-4 h-4 text-[#F97316]" />
                  <span className="text-white text-sm font-medium">100% Digital</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Zap className="w-4 h-4 text-[#F97316]" />
                  <span className="text-white text-sm font-medium">Devis gratuit</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Shield className="w-4 h-4 text-[#F97316]" />
                  <span className="text-white text-sm font-medium">Courtier indépendant</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate("/devis/auto")}
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-full px-8 py-6 text-lg font-semibold btn-lift"
                  data-testid="hero-cta-btn"
                >
                  Devis Auto & Moto
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-full px-8 py-6 text-lg"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {contactInfo.phone}
                </Button>
              </div>
            </motion.div>

            {/* License Plate Calculator - Right side - Auto & Moto */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-2xl"
            >
              {/* Vehicle type selector */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setVehicleType && setVehicleType('auto')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all ${
                    !vehicleType || vehicleType === 'auto' 
                      ? 'bg-[#102692] text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="vehicle-type-auto"
                >
                  <Car className="w-5 h-5" />
                  Auto
                </button>
                <button
                  onClick={() => setVehicleType && setVehicleType('moto')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all ${
                    vehicleType === 'moto' 
                      ? 'bg-[#102692] text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="vehicle-type-moto"
                >
                  <Bike className="w-5 h-5" />
                  Moto
                </button>
              </div>

              <h3 className="font-heading font-semibold text-lg text-slate-800 mb-2">
                Devis {vehicleType === 'moto' ? 'moto' : 'auto'} express
              </h3>
              <p className="text-slate-500 text-sm mb-4">
                Entrez votre plaque d'immatriculation pour un devis personnalisé
              </p>
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Input
                    placeholder="AB-123-CD"
                    value={licensePlate}
                    onChange={(e) => setLicensePlate(formatPlate(e.target.value))}
                    className="plate-input h-14 text-lg bg-slate-50 border-slate-200 focus:border-[#102692]"
                    data-testid="license-plate-input"
                  />
                  <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                </div>
                <Button
                  onClick={lookupPlate}
                  disabled={loading}
                  className="h-14 px-6 bg-[#102692] hover:bg-[#0D1F75] rounded-xl font-semibold"
                  data-testid="lookup-plate-btn"
                >
                  {loading ? "..." : "Rechercher"}
                </Button>
              </div>

              {vehicleInfo && !vehicleInfo.error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-4 p-4 bg-slate-50 rounded-xl"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-slate-500">Véhicule identifié</span>
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  </div>
                  <p className="font-heading font-semibold text-lg text-slate-800">
                    {vehicleInfo.brand} {vehicleInfo.model}
                  </p>
                  <p className="text-slate-600">
                    {vehicleInfo.year} • {vehicleInfo.power_cv} CV • {vehicleInfo.fuel_type}
                  </p>
                  <Button
                    onClick={() => startQuote(vehicleType || "auto")}
                    className="w-full mt-4 bg-[#F97316] hover:bg-[#EA580C] rounded-xl"
                    data-testid="start-auto-quote-btn"
                  >
                    Obtenir mon devis {vehicleType === 'moto' ? 'moto' : 'auto'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </motion.div>
              )}

              {vehicleInfo?.error && (
                <p className="mt-3 text-red-500 text-sm">{vehicleInfo.error}</p>
              )}
              
              {/* Trust indicators */}
              <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-center gap-6">
                <div className="flex items-center gap-2 text-slate-500 text-xs">
                  <Award className="w-4 h-4 text-[#F97316]" />
                  <span>Certifié ORIAS</span>
                </div>
                <div className="flex items-center gap-2 text-slate-500 text-xs">
                  <ShieldCheck className="w-4 h-4 text-[#102692]" />
                  <span>API SIV officielle</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section - White background with Orange accents */}
      <section id="produits" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-sm font-semibold text-[#F97316] uppercase tracking-wider bg-[#F97316]/10 px-4 py-1 rounded-full mb-4">Nos solutions d'assurance</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mt-3 mb-4">
              Assurances auto, habitation, santé et professionnelles
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto">
              <strong>Jyvis Assurance</strong> vous accompagne dans toute l'Île-de-France (93, 92, 94, 95, 75) et en Guadeloupe. 
              Comparez les offres et économisez sur vos primes d'assurance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product, index) => {
              const Icon = insuranceIcons[product.type] || Shield;
              const image = productImages[product.type];
              
              return (
                <motion.div
                  key={product.type}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className="insurance-card group cursor-pointer h-full hover:border-[#F97316]/30"
                    onClick={() => navigate(product.link || `/produit/${product.type}`)}
                    data-testid={`product-card-${product.type}`}
                  >
                    <CardContent className="p-0">
                      {image && (
                        <div className="relative h-32 overflow-hidden rounded-t-xl -mx-6 -mt-6 mb-4">
                          <img
                            src={image}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                        </div>
                      )}
                      <div className={image ? "p-6 pt-0" : "p-6"}>
                        <div className="w-12 h-12 bg-[#F97316]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#F97316]/20 transition-colors">
                          <Icon className="w-6 h-6 text-[#F97316]" />
                        </div>
                        <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                          {product.name}
                        </h3>
                        <p className="text-slate-500 text-sm mb-4">{product.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-[#102692] font-semibold">
                            {product.price_label || `À partir de ${product.starting_price}€/mois`}
                          </span>
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#F97316] group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Podcasts & Formation Section - New! */}
      <section id="podcasts" className="py-20 md:py-32 bg-gradient-to-br from-[#FFF7ED] to-[#FFEDD5]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-sm font-semibold text-[#F97316] uppercase tracking-wider bg-white px-4 py-1 rounded-full mb-4">
              <Headphones className="w-4 h-4 inline mr-2" />
              Podcasts & Formation
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mt-3 mb-4">
              Le podcast de Jyvis Assurance : l'assurance décryptée
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto">
              Retrouvez chaque mois un nouvel épisode pour tout comprendre sur les assurances. 
              Nos experts répondent à vos questions et vous donnent des astuces pour bien choisir vos contrats en Île-de-France et Guadeloupe.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {podcastEpisodes.map((episode, index) => (
              <motion.div
                key={episode.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="overflow-hidden bg-white hover:shadow-xl transition-all duration-300 group cursor-pointer" data-testid={`podcast-card-${episode.id}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={episode.image}
                      alt={episode.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="inline-block bg-[#F97316] text-white text-xs font-semibold px-2 py-1 rounded mb-2">
                        {episode.category}
                      </span>
                      <h3 className="text-white font-heading font-semibold text-lg">
                        {episode.title}
                      </h3>
                    </div>
                    {/* Play button overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 bg-[#F97316] rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 text-white ml-1" fill="white" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between text-slate-500 text-sm">
                      <div className="flex items-center gap-2">
                        <Headphones className="w-4 h-4" />
                        <span>Durée: {episode.duration}</span>
                      </div>
                      <span className="text-[#F97316] font-medium group-hover:underline">Écouter</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* CTA to see all podcasts */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button
              variant="outline"
              className="rounded-full px-8 py-6 border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white transition-colors"
              data-testid="view-all-podcasts-btn"
            >
              Voir tous les podcasts
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Advantages Section - White with Orange accents */}
      <section id="avantages" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-sm font-semibold text-[#102692] uppercase tracking-wider bg-[#102692]/10 px-4 py-1 rounded-full mb-4">Pourquoi nous choisir</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mt-3 mb-4">
              Vos avantages JYVIS
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: "Devis en 2 minutes",
                description: "Obtenez une estimation précise en quelques clics grâce à notre calculateur intelligent.",
                color: "#F97316"
              },
              {
                icon: Smartphone,
                title: "100% digital",
                description: "Signez vos contrats en ligne avec Yousign, sans paperasse ni déplacement.",
                color: "#102692"
              },
              {
                icon: Users,
                title: "Accompagnement expert",
                description: "Notre équipe de conseillers est disponible pour vous guider dans vos choix.",
                color: "#F97316"
              }
            ].map((advantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 bg-slate-50 rounded-2xl hover:shadow-lg transition-shadow"
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: `${advantage.color}15` }}
                >
                  <advantage.icon className="w-8 h-8" style={{ color: advantage.color }} />
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900 mb-3">{advantage.title}</h3>
                <p className="text-slate-600">{advantage.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="temoignages" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-sm font-semibold text-[#F97316] uppercase tracking-wider bg-[#F97316]/10 px-4 py-1 rounded-full mb-4">Témoignages clients</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mt-3 mb-4">
              Ils font confiance à Jyvis Assurance
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto">
              Nos clients en Île-de-France (93, 92, 94, 95, 75) et en Guadeloupe nous recommandent pour leur assurance auto, habitation et professionnelle.
            </p>
          </motion.div>

          <div className="flex justify-center mb-6">
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white border border-slate-200 shadow-sm rounded-full px-5 py-2 text-slate-700 hover:shadow-md transition-shadow" data-testid="google-reviews-badge">
              <span className="font-bold text-[#102692]">Google</span>
              <Star className="w-4 h-4 fill-[#F97316] text-[#F97316]" />
              <span className="font-bold text-slate-900" data-testid="google-reviews-rating">{GOOGLE_RATING}/5</span>
              <span className="font-semibold" data-testid="google-reviews-count">· {GOOGLE_REVIEWS_COUNT} avis clients</span>
            </a>
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-testid="google-reviews-buttons">
            <Button size="lg" onClick={() => openReviewLink(GOOGLE_REVIEWS_URL)} className="bg-[#102692] hover:bg-[#102692]/90 text-white rounded-full px-8" data-testid="google-reviews-btn">
              <Star className="w-5 h-5 mr-2 fill-[#F97316] text-[#F97316]" /> Voir nos avis Google
            </Button>
            <Button size="lg" variant="outline" onClick={() => openReviewLink(GOOGLE_LEAVE_REVIEW_URL)} className="border-[#102692] text-[#102692] rounded-full px-8" data-testid="leave-review-btn">
              <Quote className="w-5 h-5 mr-2" /> Laisser un avis
            </Button>
          </div>
        </div>
      </section>

      {/* Partners Section - Logos only integrated in background */}
      <section id="partenaires" className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="inline-block text-sm font-semibold text-[#102692] uppercase tracking-wider bg-[#102692]/10 px-4 py-1 rounded-full mb-4">Nos partenaires</span>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
              Partenaires de confiance
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center justify-center gap-10 md:gap-16"
          >
            {partners.map((partner, index) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="hover:scale-110 transition-transform duration-300"
                data-testid={`partner-${partner.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-14 md:h-20 w-auto object-contain"
                />
              </motion.div>
            ))}
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-slate-400 text-sm mt-10"
          >
            Et bien d'autres compagnies d'assurance partenaires
          </motion.p>
        </div>
      </section>

      {/* CTA Section - Orange gradient */}
      <section className="py-20 bg-gradient-to-br from-[#F97316] to-[#EA580C] relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <p className="text-white/90 font-medium mb-4">Jyvis Assurance - Courtier indépendant</p>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
            Demandez votre devis gratuit dès maintenant
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Que vous soyez en <strong>Seine-Saint-Denis (93)</strong>, <strong>Hauts-de-Seine (92)</strong>, <strong>Val-de-Marne (94)</strong>, <strong>Val-d'Oise (95)</strong>, <strong>Paris (75)</strong> ou en <strong>Guadeloupe</strong>, nous vous accompagnons.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/devis/auto")}
              className="bg-white hover:bg-white/90 text-[#F97316] rounded-full px-8 py-6 text-lg font-semibold btn-lift"
              data-testid="cta-quote-btn"
            >
              Obtenir un devis gratuit
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-full px-8 py-6 text-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              {contactInfo.phone}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer - Blue with FIXED social links */}
      <ZonesSection />

      <footer id="contact" className="bg-[#102692] text-white py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={JYVIS_LOGO} alt="Jyvis Assurance - Courtier en assurances Île-de-France et Guadeloupe" className="jyvis-logo h-14 w-auto" />
                <span className="text-white font-heading font-bold text-xl">Jyvis Assurance</span>
              </div>
              <p className="text-sm text-white/80 font-medium mb-3">
                Courtier indépendant en assurances. Présent en Seine-Saint-Denis (93), Hauts-de-Seine (92), Val-de-Marne (94), Val-d'Oise (95), Paris (75) et Guadeloupe.
              </p>
              {/* Social Media Links - FIXED with proper button structure */}
              <div className="flex items-center gap-3 mt-6">
                <a 
                  href={socialLinks.facebook}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link-btn"
                  aria-label="Facebook"
                  data-testid="social-facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a 
                  href={socialLinks.instagram}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link-btn"
                  aria-label="Instagram"
                  data-testid="social-instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a 
                  href={socialLinks.linkedin}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link-btn"
                  aria-label="LinkedIn"
                  data-testid="social-linkedin"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a 
                  href={socialLinks.twitter}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link-btn"
                  aria-label="X (Twitter)"
                  data-testid="social-twitter"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a 
                  href={socialLinks.youtube}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link-btn"
                  aria-label="YouTube"
                  data-testid="social-youtube"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
                <a 
                  href={socialLinks.tiktok}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link-btn"
                  aria-label="TikTok"
                  data-testid="social-tiktok"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold mb-4">Nos produits</h4>
              <ul className="space-y-2 text-white/70">
                <li><Link to="/produit/auto" className="hover:text-white transition-colors">Assurance Auto</Link></li>
                <li><Link to="/produit/sante" className="hover:text-white transition-colors">Assurance Santé</Link></li>
                <li><Link to="/produit/multirisque" className="hover:text-white transition-colors">Multirisque Habitation</Link></li>
                <li><Link to="/produit/vie" className="hover:text-white transition-colors">Assurance Vie</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold mb-4">Entreprise</h4>
              <ul className="space-y-2 text-white/70">
                <li><Link to="/formation-courtier" className="hover:text-white transition-colors">Formation Courtier</Link></li>
                <li><Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link></li>
                <li><Link to="/politique-de-confidentialite" className="hover:text-white transition-colors">Politique de confidentialité</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold mb-4">Contact</h4>
              <ul className="space-y-3 text-white/70">
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#F97316]" />
                  <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">
                    {contactInfo.phone}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#F97316]" />
                  <a href={`mailto:${contactInfo.email}`} className="hover:text-white transition-colors">
                    {contactInfo.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#F97316] mt-0.5" />
                  <span>{contactInfo.address}<br/>{contactInfo.city}</span>
                </li>
                <li className="flex items-start gap-3" data-testid="footer-hours">
                  <Clock className="w-5 h-5 text-[#F97316] mt-0.5" />
                  <span>Lundi–samedi : 10h–12h30 / 13h30–18h</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8 text-center text-white/50 text-sm">
            <LegalBottom />
          </div>
        </div>
      </footer>
    </div>
  );
}
